import { useState } from "react";
import { MapPin, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal } from "@/components/common/Reveal";
import { PageHero, CtaBand } from "@/components/site/PageHero";
import { IMAGES, PROJECTS } from "@/data/site";

const SECTORS = ["All", ...new Set(PROJECTS.map((p) => p.sector))];

export const ProjectCard = ({ p, onOpen, i }) => {
  const Tag = onOpen ? "button" : "div";
  return (
  <Tag {...(onOpen ? { type: "button", onClick: () => onOpen(p) } : {})} data-testid={`project-card-${p.slug}`} className="group relative block w-full overflow-hidden rounded-xl bg-[#123634] text-left">
    <img src={p.image} alt={p.title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#123634] via-[#123634]/30 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-6">
      <p className="mono text-[11px] uppercase tracking-[0.18em] text-[#E8927F]">{p.sector}</p>
      <h3 className="mt-2 text-xl text-white">{p.title}</h3>
      <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-300"><MapPin className="h-3.5 w-3.5" /> {p.city}</p>
    </div>
    {i !== undefined && <span className="mono absolute right-4 top-4 rounded bg-white/15 px-2 py-0.5 text-[11px] text-white backdrop-blur">{String(i + 1).padStart(2, "0")}</span>}
  </Tag>
  );
};

const ProjectDialog = ({ p, onClose }) => (
  <Dialog open={!!p} onOpenChange={onClose}>
    <DialogContent className="max-w-4xl overflow-hidden p-0" data-testid="project-dialog">
      {p && (
        <div className="grid md:grid-cols-5">
          <img src={p.image} alt={p.title} className="h-64 w-full object-cover md:col-span-3 md:h-full" />
          <div className="p-7 md:col-span-2">
            <p className="eyebrow">{p.sector}</p>
            <DialogTitle className="mt-2 text-2xl" style={{ fontFamily: "Fraunces, serif" }}>{p.title}</DialogTitle>
            <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {p.city}</p>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">{p.summary}</p>
            <p className="mono mt-5 text-[11px] uppercase tracking-[0.18em] text-slate-400">Scope delivered</p>
            <ul className="mt-2 space-y-1.5">{p.scope.map((s) => <li key={s} className="flex gap-2 text-sm text-slate-800"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C20500]" />{s}</li>)}</ul>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default function Projects() {
  const [sector, setSector] = useState("All");
  const [open, setOpen] = useState(null);
  const list = PROJECTS.filter((p) => sector === "All" || p.sector === sector);
  return (
    <div data-testid="projects-page">
      <PageHero eyebrow="Project showcase" title="Installations protecting sites across India." text="A selection of recent supply, installation, AMC and training engagements. Client names are withheld — references are shared with verified buyers on request." image={IMAGES.highrise} />
      <section className="container-x py-16">
        <div className="mb-8 flex flex-wrap gap-2">
          {SECTORS.map((s) => (
            <button key={s} onClick={() => setSector(s)} data-testid={`projects-filter-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${sector === s ? "border-[#123634] bg-[#123634] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"}`}>{s}</button>
          ))}
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((p, i) => <Reveal key={p.slug} delay={(i % 3) * 0.06}><ProjectCard p={p} i={i} onOpen={setOpen} /></Reveal>)}
        </div>
      </section>
      <ProjectDialog p={open} onClose={() => setOpen(null)} />
      <CtaBand />
    </div>
  );
}
